import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ImpersonationLog, AdminActionLog } from '@/types/superadmin';
import { useSuperAdminSettings } from './use-superadmin-settings';

/**
 * Audit data is only fetched when the platform audit trail is switched on.
 *
 * The backend refuses these endpoints with 403 while the trail is disabled, so
 * gating the query keeps the console clean and avoids a request that cannot
 * succeed. Checking `=== true` rather than truthiness matters: it also keeps the
 * query disabled while the settings request is still in flight, so the queries
 * do not fire speculatively and fail.
 */
export function useAuditTrailEnabled(): boolean {
  const { data: settings } = useSuperAdminSettings();
  return settings?.audit_trail_enabled === true;
}

export function useSuperAdminImpersonationLogs() {
  const enabled = useAuditTrailEnabled();
  return useQuery<ImpersonationLog[]>({
    queryKey: ['superadmin', 'impersonation-logs'],
    queryFn: async () => {
      const { data } = await api.get('/superadmin/impersonation-logs');
      return data;
    },
    enabled,
  });
}

export function useAdminAuditLogs(action?: string, limit?: number, offset?: number) {
  const enabled = useAuditTrailEnabled();
  return useQuery<AdminActionLog[]>({
    queryKey: ['superadmin', 'audit-logs', { action, limit, offset }],
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (action) params.action = action;
      if (limit) params.limit = limit;
      if (offset) params.offset = offset;
      const { data } = await api.get('/superadmin/audit-logs', { params });
      return data;
    },
    enabled,
  });
}
